import React, { useEffect, useState } from "react"
import { Button, Card, Collapse, Stack, Table } from "react-bootstrap"
import InfiniteScroll from "react-infinite-scroll-component"
import { toast } from "react-toastify"
import recordNotFound from "../../../assets/images/record_not_found.png"
import CustomSelect from "../../../components/CustomSelect"
import PageTitleButton from "../../../components/PageTitleButton"
import SearchBox from "../../../components/SearchBox"
import SweetAlerts from "../../../components/SweetAlerts"
import TooltipComponent from "../../../components/Tooltip"
import { admin } from "../../../config/apiEndpoints"
import axiosProvider from "../../../lib/AxiosProvider"
import {
  HasModulePermission,
  useHandleUnauthorizedAccess,
} from "../../../lib/GlobalMethods"
import { modulePermission, vehicleType } from "../../../lib/GlobalVariables"
import useDebounce from "../../../lib/hooks/useDebounce"
import VehicleSkeleton from "../../../skeleton/settings/VehicleSkeleton"
import BulkUpload from "./BulkUpload"
import AddEditVehicle from "./AddEditVehicle"

const VehicleManagement = () => {
  const canEdit = HasModulePermission(modulePermission.setting, "edit")
  const canDelete = HasModulePermission(modulePermission.setting, "delete")
  const canAdd = HasModulePermission(modulePermission.setting, "add")

  const [show, setShow] = useState(false)
  const [data, setData] = useState([])
  const [showAlert, setShowAlert] = useState(false)
  const [isUpdate, setIsUpdate] = useState(false)
  const [isDelete, setIsDelete] = useState(false)
  const [brandData, setBrandData] = useState([])
  const [loading, setLoading] = useState(true)
  const [showBulkUpload, setShowBulkUpload] = useState(false)

  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: false,
  })
  const [recordsCount, setRecordsCount] = useState({
    total_records: false,
    active_records: false,
    archived_records: false,
  })
  const [searchQuery, setSearchQuery] = useState("")
  const [hasMore, setHasMore] = useState(true)
  const [open, setOpen] = useState(false)
  const [filterObj, setFilterObj] = useState({
    brand_id: false,
    connector_id: false,
    vehicle_type: false,
    archived: false,
    active: false,
  })

  const [archived, setArchived] = useState(true)

  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  const handleShow = () => setShow(true)

  const handleShowBulkUpload = () => setShowBulkUpload(true)

  const handleShowAlert = () => {
    setShowAlert(true)
  }

  const { handleAccess } = useHandleUnauthorizedAccess()

  const handleClose = () => {
    setShow(false)
    setIsUpdate(false)
    setShowAlert(false)
    setIsDelete(false)
    setShowBulkUpload(false)
  }

  const handleStatusChange = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === "Active" ? "Inactive" : "Active"
      const response = await axiosProvider({
        method: "PATCH",
        endpoint: admin.updateVehicle + id,
        data: { status: newStatus },
      })

      if (response?.data?.code === 200) {
        toast.success(response.data.message)

        setRecordsCount((prevCounts) => {
          return {
            ...prevCounts,
            total_records: prevCounts.total_records,
            active_records:
              newStatus === "Active"
                ? prevCounts.active_records + 1
                : prevCounts.active_records - 1,
            inactive_records:
              newStatus === "Inactive"
                ? prevCounts.inactive_records + 1
                : prevCounts.inactive_records - 1,
          }
        })

        setData((data) =>
          data.map((vehicle) =>
            vehicle.id === id ? { ...vehicle, status: newStatus } : vehicle
          )
        )
      } else {
        throw new Error(response.data.message)
      }
    } catch (error) {
      toast.error(error.message)
    }
  }

  const fetchData = async (searchQuery) => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.vehicleList}?page=${
          pagination?.current_page
        }&search_term=${searchQuery ? searchQuery : ""}&archived=${
          filterObj?.archived
        }&brand_id=${filterObj.brand_id}&vehicle_type=${
          filterObj?.vehicle_type
        }&connector_id=${filterObj.connector_id}&active=${filterObj?.active}`,
      })

      const fetchedData = response?.data?.data || []
      setHasMore(true)

      handleAccess({ code: response?.data?.code })

      response?.data?.code === 200 && setPagination(response?.data?.pagination)
      response?.data?.code === 200 &&
        setRecordsCount(response?.data?.record_counts)

      if (
        !fetchedData.length ||
        response.data.pagination.current_page ===
          response.data.pagination.total_pages
      ) {
        setHasMore(false)
      }

      setData((prevData) =>
        pagination.current_page === 1
          ? fetchedData
          : [...prevData, ...fetchedData]
      )

      setLoading(false)
    } catch (error) {
      setLoading(false)
      toast.error(error.message || "An error occurred")
    }
  }

  const fetchBrandData = async () => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: admin.brandList,
      })
      const data = response?.data?.data.map(({ id, brand_name }) => ({
        value: id,
        label: brand_name,
      }))
      setBrandData(data)
    } catch (error) {
      toast.error(error)
    }
  }

  const fetchMoreData = () => {
    if (pagination.current_page < pagination.total_pages) {
      setPagination((prevState) => ({
        ...prevState,
        current_page: prevState.current_page + 1,
      }))
    } else {
      setHasMore(false)
    }
  }

  const handleArchive = async (id) => {
    try {
      const response = await axiosProvider({
        method: "DELETE",
        endpoint: archived
          ? `${admin.restoreVehicle + id}`
          : `${admin.deleteVehicle + id}`,
      })

      if (response?.data?.code === 200) {
        toast.success(response?.data?.message)

        setData((data) => {
          if (filterObj.archived === false && filterObj.active === false) {
            return data.map((vehicle) => {
              if (vehicle.id === id) {
                vehicle.deletedAt = archived ? null : Date.now()
              }
              return vehicle
            })
          } else {
            return data.filter((vehicle) => vehicle.id !== id)
          }
        })

        if (archived) {
          setRecordsCount((prev) => ({
            ...prev,
            active_records: prev.active_records + 1,
            archived_records: prev.archived_records - 1,
          }))
        } else {
          setRecordsCount((prev) => ({
            ...prev,
            active_records: prev.active_records - 1,
            archived_records: prev.archived_records + 1,
          }))
        }
        setLoading(false)
      } else {
        setLoading(false)
        throw new Error(response.data.message)
      }
    } catch (error) {
      setLoading(false)
      toast.error(error.message)
    }
  }

  const handleDelete = async (id) => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: "DELETE",
        endpoint: admin.deleteVehicle + id,
      })
      if (response?.data?.code === 200) {
        toast.success(response.data.message)
        setData((data) => data.filter((vehicle) => vehicle.id !== id))
        setData((data) =>
          data?.sort((value, nextValue) => nextValue.id - value.id)
        )
        setLoading(false)
      } else {
        setLoading(false)
        throw { message: response.data.message }
      }
    } catch (error) {
      setLoading(false)
      toast.error(error)
    }
  }

  useEffect(() => {
    fetchData(debouncedSearchQuery)
  }, [debouncedSearchQuery, filterObj, pagination.current_page])

  useEffect(() => {
    fetchBrandData()
  }, [])

  const filterOnClick = () => {
    setOpen(!open)
  }

  return (
    <>
      {canAdd ? (
        <PageTitleButton
          PagetitleText={"Vehicle Management"}
          HeaderButtonText={"Add Vehicle"}
          IconclassName={"plus_black"}
          onClick={handleShow}
          filterShow={true}
          filterClick={filterOnClick}
          open={open}
          DropdownMenu={
            <>
              <Button
                variant="warning"
                className="d-inline-flex gap-2 align-items-center"
                onClick={handleShowBulkUpload}
              >
                <i className={`icon_main plus_black bg-black`}></i>
                Bulk Upload
              </Button>
            </>
          }
        />
      ) : (
        <PageTitleButton
          IconclassName={"plus_black"}
          onClick={handleShow}
          filterShow={true}
          filterClick={filterOnClick}
          open={open}
        />
      )}

      <div className="details_cards_wrapper mb-3">
        <div
          className={`bg_sea_green details_card ${
            !filterObj.active && !filterObj.archived ? "card_active" : ""
          }`}
          onClick={() => {
            setFilterObj((prev) => ({
              ...prev,
              active: false,
              archived: false,
            }))
            setLoading(true)
            setPagination({ current_page: 1 })
          }}
        >
          <div className="card_titleicon">
            <h3 className="card_title">Total Vehicles</h3>
            <span className="card_icon">
              <i className="icon_main total_users"></i>
            </span>
          </div>
          <p className="tls_numbers">{recordsCount.total_records || 0}</p>
        </div>
        <div
          className={`bg_light_green details_card ${
            filterObj.active && !filterObj.archived ? "card_active" : ""
          }`}
          onClick={() => {
            setFilterObj((prev) => ({ ...prev, archived: false, active: true }))
            setPagination({ current_page: 1 })
            setLoading(true)
          }}
        >
          <div className="card_titleicon">
            <h3 className="card_title">Active Vehicles</h3>
            <span className="card_icon">
              <i className="icon_main activechecked"></i>
            </span>
          </div>
          <p className="tls_numbers">{recordsCount.active_records || 0}</p>
        </div>
        <div
          className={`bg_light_red details_card ${
            !filterObj.active && filterObj.archived ? "card_active" : ""
          }`}
          onClick={() => {
            setFilterObj((prev) => ({ ...prev, archived: true, active: false }))
            setPagination({ current_page: 1 })
            setLoading(true)
          }}
        >
          <div className="card_titleicon">
            <h3 className="card_title">Archive Vehicles</h3>
            <span className="card_icon">
              <i className="icon_main archive_data"></i>
            </span>
          </div>
          <p className="tls_numbers">{recordsCount.archived_records || 0}</p>
        </div>
      </div>

      <Collapse in={open}>
        <div id="brand-filter-collapse" className="filters_sticky">
          <Card className="mb-3">
            <Card.Body>
              <Stack direction="horizontal" gap={2} className="flex-wrap">
                <SearchBox
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e?.target?.value)
                    setPagination({ current_page: 1 })
                    setLoading(true)
                  }}
                />
                <CustomSelect
                  options={vehicleType}
                  handleSelectChange={(selectedVehicleType) => {
                    setFilterObj((prev) => ({
                      ...prev,
                      vehicle_type: selectedVehicleType
                        ? selectedVehicleType.label
                        : false,
                    }))
                    setPagination({ current_page: 1 })
                    setLoading(true)
                  }}
                  isMulti={false}
                  isSearchable={false}
                  placeholder={"Select Vehicle Type"}
                  classNameSelect={"selectdropdown_filter"}
                />

                <CustomSelect
                  options={brandData}
                  handleSelectChange={(selectBrands) => {
                    setFilterObj((prev) => ({
                      ...prev,
                      brand_id: selectBrands
                        ? selectBrands.map((status) => status.value)
                        : false,
                    }))
                    setPagination({ current_page: 1 })
                    setLoading(true)
                  }}
                  isMulti={true}
                  placeholder={"Select Brand"}
                  classNameSelect={"selectdropdown_filter"}
                />
              </Stack>
            </Card.Body>
          </Card>
        </div>
      </Collapse>

      {!loading ? (
        <Card>
          <Card.Body>
            <InfiniteScroll
              dataLength={data?.length || 10}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={<VehicleSkeleton infiniteScroll={true} rows={1} />}
            >
              <Table className="rounded mb-0 table-striped" responsive="md">
                <thead>
                  <tr>
                    <th>Details</th>
                    <th className="text-center">Battery Capacity(kW/h)</th>
                    <th className="text-center">Battery Voltage(v)</th>
                    <th className="text-center">Connectors</th>
                    {(canEdit || canDelete) && (
                      <th className="text-center">Action</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data?.length > 0 &&
                    data?.map((data, index) => (
                      <tr key={index + 1}>
                        <td>
                          <div className="vehicle_detials">
                            <img
                              src={
                                data?.vehicle_img?.startsWith("http")
                                  ? data?.vehicle_img
                                  : process.env.REACT_APP_RESOURCE_URL +
                                      data?.vehicle_img ||
                                    "https://placehold.co/50x50.png"
                              }
                              alt="vehicle"
                              className="brand_img"
                            />
                            <div className="vehiclebrds_detail">
                              <p className="vehiclebrds_label">
                                {data.brand.brand_name + " " + data.model}
                              </p>
                              <p className="vehiclebrds_label">
                                {data.vehicle_type}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="text-center">{data.battery_capacity}</td>
                        <td className="text-center">{data.battery_voltage}</td>
                        <td>
                          <div className="sppted_connectors">
                            {data?.connectors?.length > 0 &&
                              data.connectors.map((connector, key) => (
                                <div className="connector_item" key={key}>
                                  <img
                                    src={
                                      connector.connector_img
                                        ? process.env.REACT_APP_RESOURCE_URL +
                                          connector.connector_img
                                        : "https://placehold.co/50x50.png"
                                    }
                                    alt="connector_img"
                                    className="connector_item_img"
                                  />
                                  <span className="connector_nm">
                                    {connector.connector_type}
                                  </span>
                                </div>
                              ))}
                          </div>
                        </td>
                        {(canEdit || canDelete) && (
                          <td className="text-center">
                            <div className="d-inline-flex gap-2">
                              {canEdit &&
                                !filterObj.archived &&
                                data?.deletedAt === null && (
                                  <TooltipComponent
                                    toolplace="top"
                                    tooltipText="Edit"
                                  >
                                    <Button
                                      variant="btnedit_icon"
                                      className="btn_icon"
                                      onClick={() => {
                                        setIsUpdate(data)
                                        handleShow()
                                      }}
                                    >
                                      <i className="icon_main edit_pencil"></i>
                                    </Button>
                                  </TooltipComponent>
                                )}
                              {canDelete &&
                                (data?.deletedAt === null ? (
                                  <TooltipComponent
                                    toolplace="top"
                                    tooltipText="Archive"
                                  >
                                    <Button
                                      variant="btnarchive_icon"
                                      className="btn_icon"
                                      onClick={() => {
                                        setArchived(false)
                                        setShowAlert(true)
                                        setIsUpdate(data)
                                      }}
                                    >
                                      <i className="icon_main archive_data"></i>
                                    </Button>
                                  </TooltipComponent>
                                ) : (
                                  <TooltipComponent
                                    toolplace="top"
                                    tooltipText="Restore"
                                  >
                                    <Button
                                      variant="btnrestore_icon"
                                      className="btn_icon"
                                      onClick={() => {
                                        setArchived(true)
                                        setShowAlert(true)
                                        setIsUpdate(data)
                                      }}
                                    >
                                      <i className="icon_main restore_data"></i>
                                    </Button>
                                  </TooltipComponent>
                                ))}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  {data?.length === 0 && (
                    <tr>
                      <td className="text-center py-4" colSpan={6}>
                        <div className="norecord_wrapper">
                          <img src={recordNotFound} alt="No Record Found" />
                          <p className="norecords_title">No Records Found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </InfiniteScroll>
          </Card.Body>
        </Card>
      ) : (
        <VehicleSkeleton />
      )}

      <AddEditVehicle
        show={show}
        setShow={setShow}
        handleClose={handleClose}
        fetchData={fetchData}
        setIsUpdate={setIsUpdate}
        isUpdate={isUpdate}
        setData={setData}
        setIsDelete={setIsDelete}
        setShowAlert={setShowAlert}
      />

      <SweetAlerts
        showAlert={showAlert}
        alertMessage={`Are you sure you want to ${
          archived ? "Restore" : "Archive"
        } this vehicle ?`}
        handleClose={handleClose}
        iconClassName={`${
          archived ? "restore_data bg-black" : "archive_data bg-warning"
        }`}
        handleAlert={() => {
          handleArchive(isUpdate.id)
        }}
      />

      <BulkUpload
        showBulkUpload={showBulkUpload}
        setShow={setShow}
        handleClose={handleClose}
        fetchData={fetchData}
      />
    </>
  )
}

export default VehicleManagement
